/* eslint-disable react/jsx-no-bind */
import React, { useContext } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Logo from 'src/components/logo';
import { AuthContext } from 'src/auth/context';
import WalletConnectButtonClient from 'src/components/wallet-connect/WalletConnectButton-client';
import AccountChip from 'src/components/chip/AccountChip';

import { UI } from 'src/features/transfer/constants';
import { HistoryIcon } from 'src/features/transfer/components/icons';
import { getIsPhoneSession } from 'src/features/transfer/lib/format';
import NetworkStatusBanner from 'src/components/banner-switch-network/NetworkStatusBanner';
import { useResponsive } from 'src/hooks/useResponsive';

const NAV_ITEMS = [
  { label: 'How it works', to: '#how' },
  { label: 'Fees', to: '#fees' },
  { label: 'FAQ', to: '#faq' },
];

function HomeIcon(props) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V21h14V9.5" />
    </Box>
  );
}

export default function NewHeader() {
  const { user, loading } = useContext(AuthContext);
  const { pathname } = useLocation();

  const isHome = pathname === '/';
  const isPhoneSession = getIsPhoneSession(user);
  const isMobile = useResponsive('up', 'sx');

  const renderNavShortcut = () => {
    if (isHome) {
      return (
        <Box
          component={RouterLink}
          to="/transactions"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 13,
            fontWeight: 600,
            color: UI.textMuted,
            textDecoration: 'none',
            '&:hover': { color: UI.text },
          }}
        >
          <HistoryIcon sx={{ width: 16, height: 16 }} />
          Activity
        </Box>
      );
    }

    return (
      <Box
        component={RouterLink}
        to="/"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 13,
          fontWeight: 600,
          color: UI.textMuted,
          textDecoration: 'none',
          '&:hover': { color: UI.text },
        }}
      >
        <HomeIcon sx={{ width: 16, height: 16 }} />
        Home
      </Box>
    );
  };

  const renderPhoneAccount = () => {
    if (!isPhoneSession) return null;
    return <AccountChip />;
  };

  const renderAccountArea = () => {
    if (loading) {
      return <Box sx={{ width: 120, height: 34 }} />;
    }

    return (
      <Stack direction="row" alignItems="center" spacing={1.5}>
        {renderNavShortcut()}

        <Box sx={{ '& button, & > *': { borderRadius: 2 } }}>
          <WalletConnectButtonClient />
        </Box>

        {renderPhoneAccount()}
      </Stack>
    );
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        bgcolor: 'rgba(10,11,8,0.85)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${UI.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ height: 64 }}
        >
          <Stack
            component={RouterLink}
            to="/"
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{ textDecoration: 'none' }}
          >
            <Logo width={30} />
            {isMobile && (
              <Typography sx={{ fontWeight: 800, color: UI.text, letterSpacing: '-0.02em' }}>
                SafiSwap
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' } }}>
            {NAV_ITEMS.map((item) => (
              <Box
                key={item.to}
                component={RouterLink}
                to={`/${item.to}`}
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: UI.textMuted,
                  textDecoration: 'none',
                  '&:hover': { color: UI.text },
                }}
              >
                {item.label}
              </Box>
            ))}
          </Stack>

          {renderAccountArea()}
        </Stack>
        <NetworkStatusBanner />
      </Container>
    </Box>
  );
}
